import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword, 
  createUserWithEmailAndPassword as firebaseCreateUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  updateProfile,
  signOut, 
  onAuthStateChanged, 
  User, 
  UserCredential 
} from 'firebase/auth';
import { getFirestore, Firestore } from "firebase/firestore";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Auth provider
const provider = new GoogleAuthProvider();

// Force users to select an account when interacting with the provider
provider.setCustomParameters({   
  prompt: "select_account"
});

// Initialize Firebase services
export const auth = getAuth(app);
export const firestore: Firestore = getFirestore(app);

// Export auth functions
export const signInWithGooglePopup = (): Promise<UserCredential> => 
  signInWithPopup(auth, provider);

export const signInWithEmailAndPassword = (email: string, password: string): Promise<UserCredential> => 
  firebaseSignInWithEmailAndPassword(auth, email, password);

// Create new user with email and password
export const createUserWithEmailAndPassword = async (
  email: string, 
  password: string, 
  displayName: string
): Promise<UserCredential> => {
  const userCredential = await firebaseCreateUserWithEmailAndPassword(auth, email, password);
  
  // Update the user's display name
  if (userCredential.user) {
    await updateProfile(userCredential.user, { displayName });
  }
  
  // Note: We don't send Firebase verification email here
  // Our backend will send custom verification email via SendGrid
  
  return userCredential;
};

// Send email verification to current user
export const sendVerificationEmail = async (): Promise<void> => {
  const user = auth.currentUser;
  if (user) {
    const actionCodeSettings = {
      url: `${window.location.origin}/auth/action`,
      handleCodeInApp: true,
    };
    await sendEmailVerification(user, actionCodeSettings);
  } else {
    throw new Error('No user is currently signed in');
  }
};

// Send password reset email
export const sendPasswordResetEmail = (email: string): Promise<void> => 
  firebaseSendPasswordResetEmail(auth, email);

export const signOutUser = (): Promise<void> => signOut(auth);

export const onAuthStateChangedListener = (callback: (user: User | null) => void) => 
  onAuthStateChanged(auth, callback);

// Check if current user's email is verified
export const isEmailVerified = (): boolean => {
  const user = auth.currentUser;
  return user?.emailVerified ?? false;
}; 